import { extendTheme, type ThemeConfig } from '@chakra-ui/react';

/**
 * Colour-mode config. `initialColorMode: 'light'` because the composer
 * redesign is light-first (matches pictools + the marketing pages); we
 * disable `useSystemColorMode` so an explicit user pick — stored under
 * Chakra's default `chakra-ui-color-mode` localStorage key — wins over
 * OS preference. The toggle in UserMenu writes that key.
 *
 * ColorModeScript is emitted in `index.html` so the html[data-theme]
 * attribute is set BEFORE the first paint (no flash of the wrong
 * theme). See `index.html` for the inline script + `main.tsx` for the
 * `<ColorModeScript />` fallback.
 */
const config: ThemeConfig = {
  initialColorMode: 'light',
  useSystemColorMode: false,
};

const theme = extendTheme({
  config,
  colors: {
    // Composer / MixGenie brand — pink → purple. This is the visual
    // identity used by the marketing site and DLC widget; the composer
    // redesign brings it into the app itself. The `.500` sits mid-ramp
    // for text/icon accents; `.400` and `.300` are the gradient stops.
    hot: {
      50:  '#fdf2f8',
      100: '#fce7f3',
      200: '#fbcfe8',
      300: '#f9a8d4',
      400: '#f472b6',
      500: '#ec4899',
      600: '#db2777',
      700: '#be185d',
      800: '#9d174d',
      900: '#831843',
    },
    violet: {
      50:  '#faf5ff',
      100: '#f3e8ff',
      200: '#e9d5ff',
      300: '#d8b4fe',
      400: '#c084fc',
      500: '#a855f7',
      600: '#9333ea',
      700: '#7e22ce',
      800: '#6b21a8',
      900: '#581c87',
    },

    // Legacy brand palette (teal). Kept for backwards compatibility —
    // 15+ existing components reference brand.*; the composer surface
    // uses `hot`/`violet` instead and the legacy pages migrate as we
    // touch them. Do NOT delete without doing a full grep sweep.
    brand: {
      50: '#F2F8F7',
      100: '#D9ECE8',
      200: '#B8DDD6',
      300: '#8ECABF',
      400: '#62B7AA',
      500: '#2B9D8F',
      600: '#1E7E73',
      700: '#155F57',
      800: '#103F3A',
      900: '#0A2623',
    },
    accent: {
      500: '#F37A3E',
      600: '#D9622B',
    },
    // Public-catalog dark palette (homepage + Songs/Artists/SongDetail/Search).
    night: {
      900: '#0B1830',
      800: '#0F1F3D',
      700: '#15294E',
      600: '#1B3461',
      500: '#23427B',
      400: '#3D5B97',
      300: '#7388B5',
      200: '#A8B7D2',
      100: '#D5DDEC',
      50: '#EEF1F8',
    },
  },

  // Semantic tokens that swap on colour mode. Every new surface uses
  // these instead of raw palette references — that's the ONLY way the
  // theme toggle actually works. Old code still using `brand.500` etc.
  // will keep rendering as before because raw palette refs are mode-
  // insensitive.
  //
  // The naming follows a small vocabulary:
  //   bg          — page background (deepest)
  //   surface     — cards, sheets, modals
  //   surface-hi  — hovered / active surface tint
  //   surface-lo  — inset areas (workspace behind cards)
  //   line        — hairline dividers, subtle borders
  //   line-2      — emphasis borders, focused inputs
  //   fg          — primary text
  //   muted       — secondary text (~62% opacity feel)
  //   dim         — tertiary text / disabled labels
  semanticTokens: {
    colors: {
      'mg.bg':         { default: '#f5f2fa', _dark: '#0d0819' },
      'mg.surface':    { default: '#ffffff', _dark: '#171029' },
      'mg.surfaceHi':  { default: '#faf7fc', _dark: '#1e1633' },
      'mg.surfaceLo':  { default: '#efeaf5', _dark: '#120c22' },
      'mg.line':       { default: 'rgba(30, 26, 46, 0.10)', _dark: 'rgba(237, 231, 245, 0.10)' },
      'mg.line2':      { default: 'rgba(30, 26, 46, 0.18)', _dark: 'rgba(237, 231, 245, 0.18)' },
      'mg.fg':         { default: '#1e1a2e', _dark: '#ede7f5' },
      'mg.muted':      { default: 'rgba(30, 26, 46, 0.62)', _dark: 'rgba(237, 231, 245, 0.62)' },
      'mg.dim':        { default: 'rgba(30, 26, 46, 0.42)', _dark: 'rgba(237, 231, 245, 0.40)' },
    },
  },

  fonts: {
    heading: "'Poppins', sans-serif",
    body: "'Nunito', sans-serif",
  },
  styles: {
    global: {
      // The body bg now follows the mode. Old pages that assume light
      // still render fine because their own surface bgs are hard-coded.
      body: {
        bg: 'mg.bg',
        color: 'mg.fg',
      },
      a: {
        color: 'brand.700',
        _hover: { textDecoration: 'none' },
      },
    },
  },
  components: {
    Button: {
      baseStyle: {
        borderRadius: '12px',
        fontWeight: 700,
      },
      variants: {
        solid: {
          bg: 'accent.500',
          color: 'white',
          _hover: { bg: 'accent.600' },
        },
        outline: {
          borderColor: 'brand.500',
          color: 'brand.700',
          _hover: { bg: 'brand.50' },
        },
      },
    },
  },
});

export default theme;

/**
 * The mid-ramp gradient used by the composer's send button and any
 * "primary action" surface. Reserved for the ONE call-to-action per
 * screen — never use for decoration.
 */
export const MG_GRADIENT = 'linear-gradient(135deg, #ec4899 0%, #a855f7 100%)';
export const MG_GRADIENT_FLAT = 'linear-gradient(90deg, #ec4899 0%, #a855f7 100%)';
